import dynamic from 'next/dynamic';
import { memo, useEffect, useState } from 'react';

import { TalkTakeaway } from 'api';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import { sortBy } from 'lodash-es';
import { createPortal } from 'react-dom';
import { useModalStore } from '../Summary/store';

// Import ActionButtons component with correct path
const ActionButtons = dynamic(() => import('../Summary/ActionButtons'), {
  ssr: false
});

const SummaryModal = dynamic(() => import('../Summary/SummaryModal'), {
  ssr: false
});
const SummaryDrawer = dynamic(() => import('../Summary/SummaryDrawer'), {
  ssr: false
});

type SummaryComponentsProps = {
  summarizeData: TalkTakeaway[];
};

const SummaryComponents = ({ summarizeData }: SummaryComponentsProps) => {
  const { closeAllModal, isModalSummaryOpen } = useModalStore();
  const [showButtons, setShowButtons] = useState(false);

  const isMobileAndTabletWidth = useIsBreakpointWidth({
    size: 'lg',
    breakPointType: 'tui'
  });

  // Ensure buttons render after modal
  useEffect(() => {
    if (isModalSummaryOpen) {
      // Short delay to ensure modal is fully rendered before buttons
      const timer = setTimeout(() => {
        setShowButtons(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowButtons(false);
    }
  }, [isModalSummaryOpen]);

  if (summarizeData?.length === 0) return <></>;

  const summarySorted = sortBy(summarizeData, 'offsetSeconds');
  if (isMobileAndTabletWidth && isModalSummaryOpen) {
    return (
      <div className="relative h-full">
        {/* Modal content */}
        <SummaryModal
          isModalOpen={isModalSummaryOpen}
          summaryData={summarySorted}
          closeModal={closeAllModal}
        />

        {/* Fixed action buttons at bottom of modal container */}
        {showButtons &&
          createPortal(
            <div
              className="pointer-events-auto fixed left-0 right-0 z-[999] bg-white/10 backdrop-blur-sm"
              style={{
                bottom: 0,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)'
              }}
              onClick={e => e.stopPropagation()}
              aria-hidden="false"
            >
              <ActionButtons />
            </div>,
            document.body
          )}
      </div>
    );
  }

  if (!isMobileAndTabletWidth && isModalSummaryOpen) {
    return (
      <SummaryDrawer
        isModalOpen={isModalSummaryOpen}
        summaryData={summarySorted}
        closeModal={closeAllModal}
      />
    );
  }

  return null;
};

export default memo(SummaryComponents);
